// Orçamento importado do ERP — hoje, o orçamento da Omie (ListarOrcamentos, ver OmieBudgetImportContext):
// o valor previsto de cada categoria x mês, em módulo.
//
// A importação lança em R$ só as categorias que vieram com orçamento: os 12 meses delas passam a ser
// os importados (mês sem valor = 0). As demais categorias não mudam.
//
// O valor exato só existe no modo valor (R$). No pré-preenchido (%), o plano passa para R$ e as demais
// categorias ficam com o projetado atual, congelado em R$ — assim nada além das importadas muda.
//
// Sinal: pela natureza da própria categoria no ERP (P = despesa, negativo); sem natureza, pelo sinal
// natural da linha (ver valueMode.naturalSign).

import { MONTH_INDEXES, zeros12 } from './periods';
import { emptyAdjustments, INPUT_MODE, isOutsidePlanned, isValueMode, TARGET } from './adjustments';
import { parseCategoryNature } from './erpCategories';
import { naturalSign } from './valueMode';
import { projectCategories } from './simulate';

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/** ERP Omie (enum 0 do backend; aceita também o nome). */
export const isOmieErp = (erp) =>
  erp != null && erp !== '' && (Number(erp) === 0 || String(erp).trim().toLowerCase() === 'omie');

/**
 * Agrupa os itens importados ({ categoriaId, mes 1–12, valorMedio, categoriaNatureza }) pelas
 * categorias do plano (código sem diferenciar maiúsculas). Itens sem valor não contam.
 * Retorna { series: Map(cod -> number[12], já com sinal), ignored: códigos que não estão no plano }.
 */
export const importedBudgetSeries = (model, items) => {
  const canonical = new Map(model.catList.map((cod) => [String(cod).trim().toLowerCase(), cod]));
  const series = new Map();
  const ignored = new Set();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const raw = String(item?.categoriaId ?? item?.CategoriaId ?? '').trim();
    const month = Number(item?.mes ?? item?.Mes);
    const value = Math.abs(Number(item?.valorMedio ?? item?.ValorMedio) || 0);
    if (!raw || !(month >= 1 && month <= 12) || value < 0.005) return;
    const cod = canonical.get(raw.toLowerCase());
    if (!cod) {
      ignored.add(raw);
      return;
    }
    const nature = parseCategoryNature(item?.categoriaNatureza ?? item?.CategoriaNatureza);
    const sign = nature ? (nature === 'R' ? 1 : -1) : naturalSign(model, TARGET.CATEGORY, cod);
    if (!series.has(cod)) series.set(cod, zeros12());
    const target = series.get(cod);
    target[month - 1] = round2(target[month - 1] + sign * value);
  });
  return { series, ignored: [...ignored] };
};

/** Lança o orçamento importado (`series` de importedBudgetSeries) no plano consolidado. */
export const applyImportedBudget = (model, state, series) => {
  const imported = Object.fromEntries([...series].map(([cod, values]) => [cod, [...values]]));
  if (isValueMode(state)) return { ...state, values: { ...(state.values || {}), ...imported } };

  // Pré-preenchido: congela o projetado atual em R$. Valores lançados em categorias que não estão no
  // modelo (ex.: migradas do orçamento do Sync) seguem como estão.
  const values = {};
  Object.entries(state.values || {}).forEach(([cod, v]) => {
    if (!model.categories.has(cod)) values[cod] = v;
  });
  const { projected } = projectCategories(model, state);
  projected.forEach((series12, cod) => {
    if (model.outsideCatSet?.has(cod) && !isOutsidePlanned(model, state, cod)) return;
    const frozen = MONTH_INDEXES.map((m) => round2(series12[m]));
    if (frozen.some((v) => v)) values[cod] = frozen;
  });
  return {
    ...emptyAdjustments(INPUT_MODE.VALUE),
    ...(state.outside ? { outside: state.outside } : {}),
    values: { ...values, ...imported },
  };
};
